/**
 * Shared calendar configurations.
 * These are reused across multiple role configs.
 */

import { BookingMode } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { RoleConfig } from './types'
import { employmentTypes } from '../constants/appConstants'

type CalendarConfig = NonNullable<RoleConfig['inProductCalendar']>

/**
 * DA Therapist calendar - hybrid mode with full popup actions
 */
export const DA_THERAPIST_CALENDAR: CalendarConfig = {
  hybridBookingMode: true,
  subCalendarSelection: true,
  hideCalendarEventDetails: false,
  displayAppointmentBookedDetails: false,
  showCalendarPopupReschedule: true,
  showCalendarPopupCancel: true,
  showCalendarPopupClose: true,
  showExpandableWorkingHours: true,
  showModalitySelection: true,
  triggerOnOpenPopover: true,
  showSessionSubmission: true,
  showCalendarScheduleSession: true,
  expandedStartTime: '03:00:00',
  expandedEndTime: '00:00:00',
  enableBackdatedAppointments: true,
  showInactiveClientsForScheduling: true,
  showLateCancellations: true,
  enableSessionModalityEditability: true,
}

/**
 * BC Therapist calendar - standard calendar mode with expandable hours
 */
export const BC_THERAPIST_CALENDAR: CalendarConfig = {
  hybridBookingMode: false,
  subCalendarSelection: false,
  hideCalendarEventDetails: false,
  displayAppointmentBookedDetails: false,
  showCalendarPopupReschedule: true,
  showCalendarPopupCancel: true,
  showCalendarPopupClose: true,
  showExpandableWorkingHours: true,
  showModalitySelection: false,
  triggerOnOpenPopover: true,
  showCalendarScheduleSession: true,
}

/**
 * BC Prescriber calendar - similar to therapist but may differ
 */
export const BC_PRESCRIBER_CALENDAR: CalendarConfig = {
  hybridBookingMode: false,
  subCalendarSelection: false,
  hideCalendarEventDetails: false,
  displayAppointmentBookedDetails: false,
  showExpandableWorkingHours: true,
  showModalitySelection: false,
  triggerOnOpenPopover: true,
  showCalendarScheduleSession: true,
}

/**
 * Coach calendar with bookable targets for different employment types
 */
export const COACH_CALENDAR: CalendarConfig = {
  hybridBookingMode: false,
  subCalendarSelection: false,
  hideCalendarEventDetails: false,
  displayAppointmentBookedDetails: false,
  showCalendarPopupReschedule: true,
  showCalendarPopupCancel: true,
  showCalendarPopupClose: true,
  showExpandableWorkingHours: false,
  triggerOnOpenPopover: true,
  showCalendarScheduleSession: true,
  showModalitySelection: false,
  bookableTarget: {
    [employmentTypes.FULL_TIME]: 30,
    [employmentTypes.PART_TIME]: 16,
    [employmentTypes.FLEX]: 7,
    [employmentTypes.TEST]: 3,
  },
  bookableMax: {
    [employmentTypes.FULL_TIME]: 40,
    [employmentTypes.PART_TIME]: 28,
    [employmentTypes.FLEX]: 15,
    [employmentTypes.TEST]: 5,
  },
  workingHours: {
    [employmentTypes.FULL_TIME]: 40,
    [employmentTypes.PART_TIME]: 21,
    [employmentTypes.FLEX]: undefined,
    [employmentTypes.TEST]: 5,
  },
  setupCalendarZendeskArticle:
    'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/43211477196819-Lyra-Engage-Platform-Calendar-Schedule-Feature-How-to-Set-up-your-Lyra-Calendar',
  manageCalendarZendeskArticle:
    'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/43211993826579-Lyra-Engage-Platform-Calendar-Schedule-Feature-Day-to-Day-Calendar-Management',
}

/**
 * BC DBT Therapist calendar - same as BC Therapist but with DBT-specific bookable targets.
 * DBT providers have different calendar spot expectations (e.g., FT=28 instead of 30).
 */
export const BC_DBT_THERAPIST_CALENDAR: CalendarConfig = {
  ...BC_THERAPIST_CALENDAR,
  bookableTarget: {
    [employmentTypes.FULL_TIME]: 28,
  },
  workingHours: {
    [employmentTypes.PART_TIME]: 20,
    [employmentTypes.REDUCED_PART_TIME]: 20,
    [employmentTypes.FULL_TIME]: 40,
    [employmentTypes.ENHANCED_PART_TIME]: 26,
    [employmentTypes.REDUCED_FULL_TIME]: 40,
    [employmentTypes.FELLOW_SUPERVISOR]: 34,
    [employmentTypes.CLINICAL_QUALITY_COORDINATOR]: 40,
    [employmentTypes.TEST]: 5,
  },
}

/**
 * BC DBT Teens Therapist calendar - DBT Teens providers share the DBT calendar spot
 * expectations (FT=28), tracked separately so the two programs can diverge.
 */
export const BC_DBT_TEENS_THERAPIST_CALENDAR: CalendarConfig = {
  ...BC_DBT_THERAPIST_CALENDAR,
  bookableTarget: {
    [employmentTypes.FULL_TIME]: 28,
  },
}

/**
 * Minimal calendar for supervisors and admins
 */
export const MINIMAL_CALENDAR: CalendarConfig = {
  hideCalendarEventDetails: false,
  displayAppointmentBookedDetails: false,
}

/**
 * Hybrid calendar for individual providers (DA-only providers)
 */
export const HYBRID_PROVIDER_CALENDAR: CalendarConfig = {
  hybridBookingMode: true,
  subCalendarSelection: true,
  hideCalendarEventDetails: true,
  displayAppointmentBookedDetails: true,
  showCalendarPopupReschedule: false,
  showCalendarPopupCancel: false,
  showCalendarPopupClose: false,
  showExpandableWorkingHours: false,
  showModalitySelection: true,
  triggerOnOpenPopover: false,
  showCalendarScheduleSession: false,
  showLegacyScheduleSession: true,
}

/**
 * ICAS provider calendar with international links
 */
export const ICAS_CALENDAR: CalendarConfig = {
  hybridBookingMode: true,
  subCalendarSelection: true,
  hideCalendarEventDetails: true,
  displayAppointmentBookedDetails: true,
  showCalendarPopupReschedule: false,
  showCalendarPopupCancel: false,
  showCalendarPopupClose: false,
  showExpandableWorkingHours: false,
  useInternationalLinks: true,
  showModalitySelection: true,
  triggerOnOpenPopover: false,
  showCalendarScheduleSession: false,
}

/**
 * Practice provider calendar
 */
export const PRACTICE_PROVIDER_CALENDAR: CalendarConfig = {
  hybridBookingMode: true,
  subCalendarSelection: true,
  hideCalendarEventDetails: true,
  displayAppointmentBookedDetails: true,
  showExpandableWorkingHours: false,
  showModalitySelection: true,
  triggerOnOpenPopover: false,
  showLegacyScheduleSession: true,
}

/**
 * BEND Core DA (1099) provider calendar
 * Core providers with Direct Access (1099 contractors)
 * Booking mode: hybrid_block
 * Clinic hours: 3am-12am, all 7 days
 */
export const BEND_CORE_DA_CALENDAR: CalendarConfig = {
  simplifiedSetupFlow: true,
  showDetailedSetupCompleteMessage: true,
  bookingMode: BookingMode.HYBRID_BLOCK,
  flexibleAvailabilityWindows: true,
  hideBookableTarget: true,
  showTimezoneSelector: true,
  setupCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  manageCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  dailyStart: '03:00:00',
  dailyEnd: '00:00:00',
}

/**
 * BEND Core LT (W2) provider calendar
 * Core providers with Lyra Therapy (W2 employees)
 * Booking mode: bookable_block
 * Clinic hours: 3am-12am, all 7 days
 */
export const BEND_CORE_LT_CALENDAR: CalendarConfig = {
  simplifiedSetupFlow: true,
  showDetailedSetupCompleteMessage: false,
  bookingMode: BookingMode.BOOKABLE_BLOCK,
  flexibleAvailabilityWindows: true,
  hideBookableTarget: true,
  showTimezoneSelector: true,
  setupCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  manageCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  dailyStart: '03:00:00',
  dailyEnd: '00:00:00',
}

/**
 * BEND NP DA (1099) provider calendar
 * Neuropsych providers with Direct Access (1099 contractors)
 * Booking mode: hybrid_managed
 * Clinic hours: 7am-7pm, Monday-Saturday (no Sunday)
 */
export const BEND_NP_DA_CALENDAR: CalendarConfig = {
  simplifiedSetupFlow: true,
  showNpSessionSelection: true,
  showDetailedSetupCompleteMessage: true,
  bookingMode: BookingMode.HYBRID_MANAGED,
  hideBookableTarget: true,
  showTimezoneSelector: true,
  setupCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  manageCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  dailyStart: '07:00:00',
  dailyEnd: '19:00:00',
  hiddenDays: [0],
}

/**
 * BEND NP LT (W2) provider calendar
 * Neuropsych providers with Lyra Therapy (W2 employees)
 * Booking mode: hybrid_managed
 * Clinic hours: 7am-7pm, Monday-Saturday (no Sunday)
 */
export const BEND_NP_LT_CALENDAR: CalendarConfig = {
  simplifiedSetupFlow: true,
  showNpSessionSelection: true,
  showDetailedSetupCompleteMessage: true,
  bookingMode: BookingMode.HYBRID_MANAGED,
  hideBookableTarget: true,
  showTimezoneSelector: true,
  setupCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  manageCalendarZendeskArticle: 'https://bend-providers.zendesk.com/hc/en-us/',
  dailyStart: '07:00:00',
  dailyEnd: '19:00:00',
  hiddenDays: [0],
}
